import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import GridFilterPanel from '../../commons/filters/GridFilterPanel.jsx';
import * as Utils from '../../commons/utils/Utils.js';
import {
	COMBUSTIBLES_FILTROS_STORAGE_KEY,
	buildCombustiblesFilterFields,
	getCombustiblesFilterDefaults,
} from './combustiblesFilterFields.js';

/**
 * Filtros de la grilla de Combustibles (VEC-3693).
 *
 * El panel escrito a mano eran ~426 líneas de `<Collapse>` dentro de `CombustiblesGridNew.js`,
 * más un `useState` por filtro que leía `localStorage` en línea y un switch "Persistir Filtros".
 * Acá queda sólo lo que el patrón no puede saber: de dónde salen las opciones.
 *
 * *** LAS OPCIONES LAS CARGA LA GRILLA, NO ESTE WRAPPER. *** No es el reparto habitual, pero
 * acá la mayoría —`controles`, `marcas`, `motivosValidez`— las usa también el cuerpo de la
 * grilla para pintar las filas, así que pedirlas dos veces sería peor. Llegan por `options`.
 * La única que es sólo del filtro es `modelo`, y esa sí vive acá.
 */

const CombustiblesGridFiltros = (props) => {
	const intl = useIntl();

	const fields = buildCombustiblesFilterFields(intl, props.flags);

	/**
	 * *** `modelo` DEPENDE DE `marca`. *** Mismo caso que Móviles Inmovilizados, y valen las
	 * mismas reglas (ver docs/design-system/grid-filter-panel.md). Desde la fase 2 `marca` es
	 * MÚLTIPLE, así que los modelos son la UNIÓN de los de cada marca elegida.
	 *
	 * Se cachean POR MARCA para que la poda de los modelos huérfanos sea SINCRÓNICA: sacar una
	 * marca no necesita ir a la red para saber qué modelos dejaron de pertenecerle.
	 */
	const modelosPorMarca = useRef({});
	const enVuelo = useRef({});
	const marcasVigentes = useRef([]);
	const modelosDelEstadoInicial = useRef(false);
	const borrador = useRef(null);

	const [modelos, setModelos] = useState([]);
	const [correccion, setCorreccion] = useState(null);

	/**
	 * La grilla también puede imponerle un filtro al panel: entrar desde el widget de cargas
	 * inválidas deja Estado en "Inválidas". Es el mismo mecanismo que usa la poda del modelo,
	 * así que comparten el `correccion`: los dos sincronizan el control sin disparar `onApply`.
	 */
	useEffect(() => {
		if (!props.overrideState) return;
		// *** ESTE NO VA COMO `draftOnly`. *** `handleInvalidas` YA cambió la consulta de la
		// grilla, así que no es una corrección del borrador: es el caso de las tarjetas de KPI
		// de tickets. Si no se aplica también al estado aplicado, lo elegido no se persiste y
		// al reabrir la pantalla vuelve el estado anterior (review del PR 2494).
		setCorreccion({ valores: props.overrideState, soloBorrador: false });
		if (props.onOverrideHandled) props.onOverrideHandled();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.overrideState]);

	const idsDe = (opciones) =>
		(Array.isArray(opciones) ? opciones : [])
			.filter((o) => o && o.value !== undefined && o.value !== null)
			.map((o) => o.value);

	const opcionesDeMarcas = (marcas) => {
		const porValue = {};
		marcas.forEach((id) => {
			(modelosPorMarca.current[id] || []).forEach((m) => {
				porValue[m.value] = m;
			});
		});
		return Object.values(porValue);
	};

	/**
	 * Al sacar una marca se van sus modelos elegidos: si no, la consulta sale con una
	 * combinación imposible y el reporte devuelve cero filas sin ningún aviso.
	 *
	 * *** LA VALIDEZ DEPENDE SÓLO DE LAS MARCAS QUE QUEDAN. *** Si falta cargar la lista de
	 * alguna de ellas se DIFIERE, porque los chips los repone localStorage antes que las
	 * opciones y darlos por huérfanos ahí borraría un filtro válido que el usuario guardó. No
	 * se difiere por la marca que se sacó: su respuesta puede no llegar nunca.
	 */
	const podarModelosElegidos = (elegidos, marcas) => {
		if (!elegidos || elegidos.length === 0) return;
		if (marcas.some((id) => !modelosPorMarca.current[id])) return;

		const validos = {};
		marcas.forEach((id) => {
			modelosPorMarca.current[id].forEach((m) => { validos[String(m.value)] = true; });
		});

		const sobreviven = elegidos.filter((o) => o && validos[String(o.value)]);
		if (sobreviven.length === elegidos.length) return;

		if (borrador.current) borrador.current = Object.assign({}, borrador.current, { modelo: sobreviven });
		// La poda SÍ es del borrador: el usuario no buscó esto, así que no se persiste.
		setCorreccion({ valores: { modelo: sobreviven }, soloBorrador: true });
	};

	const refrescarModelos = () => {
		setModelos(opcionesDeMarcas(marcasVigentes.current));
		if (borrador.current) podarModelosElegidos(borrador.current.modelo, idsDe(borrador.current.marca));
	};

	/** Pide los modelos de UNA marca y los cachea apenas llegan, sin esperar a las otras. */
	const pedirModelosDe = (id) => {
		if (modelosPorMarca.current[id] || enVuelo.current[id]) return;
		enVuelo.current[id] = true;

		Utils.getData('/modelos/marca/' + id + '/select')
			.then((data) => {
				modelosPorMarca.current[id] = data || [];
			})
			.catch(() => {})
			.then(() => {
				delete enVuelo.current[id];
				refrescarModelos();
			});
	};

	const cargarModelos = (marcas, modelosElegidos) => {
		marcasVigentes.current = marcas;
		setModelos(opcionesDeMarcas(marcas));
		podarModelosElegidos(modelosElegidos, marcas);
		marcas.forEach(pedirModelosDe);
	};

	const handleFieldChange = (name, valor, estado) => {
		borrador.current = estado || null;
		if (name !== 'marca') return;

		cargarModelos(idsDe(Array.isArray(valor) ? valor : [valor]), (estado && estado.modelo) || []);
	};

	/**
	 * *** `applyOnMount` NO DISPARA `onFieldChange`, SÓLO `onApply`. ***
	 * Al reingresar con marcas persistidas los chips se ven, pero el desplegable de modelos
	 * queda vacío porque nadie pidió sus opciones. Se resuelve leyendo el query que el panel
	 * entrega al montar, que ya trae las marcas serializadas ("3,5").
	 */
	const handleApply = (query) => {
		if (!modelosDelEstadoInicial.current) {
			modelosDelEstadoInicial.current = true;
			const marcas = String(query.marca || '').split(',').filter((v) => v !== '');
			if (marcas.length > 0) marcasVigentes.current = marcas;
		}

		// Un pedido que falló se reintenta acá, que es cuando el usuario está usando el filtro.
		marcasVigentes.current.forEach(pedirModelosDe);

		if (props.onApply) props.onApply(query);
	};

	return (
		<GridFilterPanel
			open={props.open}
			fields={fields}
			options={Object.assign({}, props.options, { modelo: modelos })}
			defaults={getCombustiblesFilterDefaults(intl)}
			storageKey={COMBUSTIBLES_FILTROS_STORAGE_KEY}
			loading={props.loading}
			resetSignal={props.resetSignal}
			onResetSignalHandled={() => {
				borrador.current = null;
				if (props.onResetSignalHandled) props.onResetSignalHandled();
			}}
			onFieldChange={handleFieldChange}
			overrideState={correccion ? correccion.valores : null}
			overrideSignal={correccion}
			overrideMerge
			overrideDraftOnly={correccion ? correccion.soloBorrador : true}
			onOverrideSignalHandled={() => setCorreccion(null)}
			onApply={handleApply}
			applyOnMount
		/>
	);
};

export default CombustiblesGridFiltros;
